/**
 * index.js — состояние монтажного стола и проводка.
 *
 * Здесь живёт ОДНА копия правды: `state.main` + `state.clips`. Всё остальное
 * (таймлайн, инспектор, предпросмотр, экспорт) читает её и ничего своего не
 * помнит — прежняя версия держала режим в трёх местах, длительность результата
 * считала дважды, а граф экспорта строила из четвёртой копии.
 */
import { KINDS, clipDuration, outputDuration, validate, toOutput, atOn, cuts, overlays } from './model.js';
import { probeVideo, probeImage, grabThumb, filmstrip, peaks, fmtTime, fmtTimeMs, fmtSize } from './media.js';
import { stillFrom } from './sources.js';
import { createTimeline } from './timeline.js';
import { createPreview } from './preview.js';
import { createInspector } from './cards.js';
import { runExport, chooseTarget } from './export.js';
import { pausesFromPeaks, scenesFrom, mergeAnchors, snap } from './anchors.js';

const SLUG = 'video-insert';
const $ = function (id) { return document.getElementById(id); };
const CFG = JSON.parse(($('vi-config') || {}).textContent || '{}');
const L = CFG.lang || {};
const T = CFG.transitions || [];

function ae(name, data) { try { window.__ae && window.__ae(name, Object.assign({ slug: SLUG }, data || {})); } catch (e) { } }
function stage(n) { try { window.__stage && window.__stage(n); } catch (e) { } }
function toast(m, t) { try { window.__toast && window.__toast(m, t || 'info'); } catch (e) { } }

/* `queue` — ДОПОЛНИТЕЛЬНЫЕ основные ролики для пакета. Первый выбранный файл
   остаётся тем, на котором идёт монтаж: клипы настраиваются один раз, а
   применяются ко всем. Здесь лежат только паспорта (имя/размер) — сами файлы
   разбираются по одному в момент экспорта, иначе двадцать видео означали бы
   двадцать живых <video> и декодированных дорожек в памяти сразу. */
var state = { main: null, clips: [], selected: null, nextId: 1, queue: [] };
var undoStack = [], redoStack = [];
var tl = null, pv = null, insp = null;
var stripAbort = null, exporting = null, resultUrl = null, wired = false;
/* Подсказки «куда вставить»: смены плана + паузы. Считаются в фоне и
   ничего не блокируют — их отсутствие это просто отсутствие подсказок. */
var anchors = [], anchorAbort = null, snapOn = true, lastSnap = null;

/* ─────────── история ───────────
   Снимок — это структура БЕЗ файлов и элементов: File и <video> в JSON не
   сериализуются, а по id всё восстанавливается из живого списка. */
function snapshot() {
    return JSON.stringify(state.clips.map(function (c) {
        var o = {};
        Object.keys(c).forEach(function (k) {
            if (['file', 'url', 'el', 'img', 'bitmap', 'strip', 'onStrip', 'thumb', 'stillUrl'].indexOf(k) >= 0) return;
            o[k] = c[k];
        });
        return o;
    }));
}
function pushUndo() {
    undoStack.push(snapshot());
    if (undoStack.length > 60) undoStack.shift();
    redoStack.length = 0;
    syncHistoryButtons();
}
function applySnapshot(json) {
    var arr = JSON.parse(json), byId = {};
    state.clips.forEach(function (c) { byId[c.id] = c; });
    var next = [];
    arr.forEach(function (o) {
        var c = byId[o.id];
        if (!c) return;                 // файл этого клипа уже отпущен — не выдумываем
        Object.keys(o).forEach(function (k) { c[k] = o[k]; });
        next.push(c);
    });
    state.clips = next;
    renderAll();
}
function undo() {
    if (!undoStack.length) return;
    redoStack.push(snapshot());
    applySnapshot(undoStack.pop());
    syncHistoryButtons();
    ae('feature_use', { f: 'undo' });
}
function redo() {
    if (!redoStack.length) return;
    undoStack.push(snapshot());
    applySnapshot(redoStack.pop());
    syncHistoryButtons();
}
function syncHistoryButtons() {
    $('vi-undo').disabled = !undoStack.length;
    $('vi-redo').disabled = !redoStack.length;
}

/* ─────────── загрузка основного файла ─────────── */
async function loadMain(file) {
    stage('file_loaded');
    try {
        var m = await probeVideo(file);
        state.main = Object.assign(m, { volume: 100, mute: false, size: file.size });
        $('vi-video').src = m.url;
        $('vi-name').textContent = m.name;
        $('vi-dims').textContent = m.width + '×' + m.height + ' · ' + fmtTime(m.duration) + ' · ' + fmtSize(file.size);
        $('vi-app').hidden = false;
        $('vi-badge').hidden = m.hasAudio;
        if (!m.hasAudio) $('vi-badge').textContent = L.no_audio;

        boot();
        buildStrip();
        buildWave();
        buildScenes();
        renderQueue();
        renderAll();
        ae('feature_use', { f: 'main_loaded', dur: Math.round(m.duration), w: m.width, h: m.height });
    } catch (e) {
        toast(L.load_failed || L.error, 'error');
        try { window.__aeToolError && window.__aeToolError({ error_type: 'vi_main_load', err_msg: String(e && e.message || e).slice(0, 200) }); } catch (x) { }
    }
}

function boot() {
    if (tl) return;
    tl = createTimeline({
        box: $('vi-tlbox'), inner: $('vi-tlinner'),
        film: $('vi-film'), wave: $('vi-wave'), playhead: $('vi-ph')
    });
    tl.setDuration(state.main.duration);

    pv = createPreview({
        screen: $('vi-screen'), frame: $('vi-frame'), video: $('vi-video'),
        getState: function () { return state; },
        onTime: function (out, mainT, total) {
            $('vi-clock').innerHTML = '<b>' + fmtTime(out) + '</b> / ' + fmtTime(total);
            if (mainT != null) tl.setPlayhead(mainT);
        },
        onSelect: function (id) { state.selected = id; insp.render(state.main, state.clips, id); tl.setMarks(state.main, state.clips, id); },
        onChange: function (kind, clip) {
            if (kind === 'play' || kind === 'pause') {
                $('vi-ico-play').hidden = pv.playing;
                $('vi-ico-pause').hidden = !pv.playing;
            }
            if (kind === 'pip-begin') pushUndo();
            if (kind === 'pip-commit') { insp.render(state.main, state.clips, state.selected); ae('feature_use', { f: 'pip_dragged' }); }
        }
    });

    insp = createInspector({
        list: $('vi-list'), lang: L, transitions: T,
        playheadMain: function () { return currentMainTime(); },
        onChange: function (c, what) {
            /* ⚠️ ЖИВОЕ перетаскивание не имеет права перерисовывать карточки:
               insp.render() пересобирает их DOM, и ручка, за которую держится
               указатель, исчезает вместе с pointer capture — тяга обрывается
               через ~20 px. Замерено: обрезка вместо 8→5 с давала 8→7.5 с.
               Поэтому на кадре тяги обновляем только лёгкое (метки, итог), а
               полную перерисовку делаем на отпускании. */
            if (what === 'trim' || what === 'pip') { lightRefresh(); return; }
            if (what === 'place') { pv.sync(); }
            pushUndo();
            renderAll(true);
            if (what === 'place') ae('feature_use', { f: 'place_changed', to: c.place });
        },
        onRemove: function (c) { removeClip(c); },
        onSelect: function (id) {
            state.selected = id;
            pv.select(id);
            tl.setMarks(state.main, state.clips, id);
            var c = byId(id);
            if (c) pv.seek(toOutput(state.main, state.clips, atOn(state.main, c)) + 0.05);
        },
        onPickTransition: openTransitionPicker
    });

    /* ⚠️ Слушатели вешаются ОДИН раз за жизнь страницы, а не на каждый boot().
       Убрать файл и загрузить новый — обычный путь («Другой файл» в общем
       аплоадере), и boot() тогда зовётся снова: без этой заслонки каждое
       нажатие Delete удаляло бы ДВЕ вставки, а стрелка прыгала на два кадра.
       Сами узлы (#vi-tlbox, #vi-screen) переживают сброс, поэтому и подписки
       на них переживать обязаны. */
    if (!wired) {
        wired = true;
        wireTimeline();
        wireKeys();
        window.addEventListener('resize', onResize);
        if (window.ResizeObserver) new ResizeObserver(onResize).observe($('vi-screen'));
    }
    requestAnimationFrame(function () { pv.layout(); tl.resize(); });
}

var resizeTimer = null;
function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () { if (pv) pv.layout(); if (tl) tl.resize(); }, 120);
}

function byId(id) { for (var i = 0; i < state.clips.length; i++) if (state.clips[i].id === id) return state.clips[i]; return null; }
function currentMainTime() {
    if (!state.main) return 0;
    var v = $('vi-video');
    return Math.max(0, Math.min(state.main.duration, v.currentTime || 0));
}

/* ─────────── лента кадров и волна ─────────── */
async function buildStrip() {
    if (stripAbort) stripAbort.abort();
    stripAbort = new AbortController();
    var sig = stripAbort.signal;
    var tiles = [];
    var v = document.createElement('video');
    v.preload = 'auto'; v.muted = true; v.playsInline = true; v.src = state.main.url;
    await new Promise(function (r) { v.onloadedmetadata = r; v.onerror = r; setTimeout(r, 8000); });
    var n = Math.max(8, Math.min(30, Math.round(($('vi-tlbox').clientWidth || 700) / 44)));
    await filmstrip(v, state.main.duration, n, function (i, total, tile) {
        if (sig.aborted) return;
        tiles[i] = tile && tile.width ? tile : null;
        if (tile && !tile.width) { var im = new Image(); im.src = tile; tiles[i] = im; }
        tl.setTiles(tiles);
        if (i === total - 1) $('vi-tlload').hidden = true;
    }, sig);
    $('vi-tlload').hidden = true;
    try { v.removeAttribute('src'); v.load(); } catch (e) { }
}
async function buildWave() {
    if (!state.main.hasAudio) return;
    var p = await peaks(state.main.file, 1400, SLUG);
    if (!p) return;
    tl.setPeaks(p);
    // Паузы берём из ТЕХ ЖЕ пиков — второй проход по файлу не нужен.
    mergeIn(pausesFromPeaks(p, state.main.duration).map(function (x) {
        return { t: x.mid, kind: 'pause', len: x.len };
    }), 'pause');
}

/* Смены плана — в фоне. На 20-секундном файле это ~300 мс (замер), на длинном
   секунды; поэтому за пределами разумной длины не начинаем вовсе: подсказка не
   стоит того, чтобы греть телефон десять минут. */
async function buildScenes() {
    if (!state.main || state.main.duration > 20 * 60) return;
    if (anchorAbort) anchorAbort.abort();
    anchorAbort = new AbortController();
    var sig = anchorAbort.signal;
    try {
        var sc = await scenesFrom(state.main.file, { signal: sig });
        if (sig.aborted) return;
        mergeIn(sc.map(function (t) { return { t: t, kind: 'scene' }; }), 'scene');
        ae('feature_use', { f: 'anchors_found', scenes: sc.length, dur: Math.round(state.main.duration) });
    } catch (e) { /* нет подсказок — тул работает как раньше */ }
}

function mergeIn(list, kind) {
    anchors = mergeAnchors(
        anchors.filter(function (a) { return a.kind === 'scene'; }).map(function (a) { return a.t; })
            .concat(kind === 'scene' ? list.map(function (a) { return a.t; }) : []),
        anchors.filter(function (a) { return a.kind === 'pause'; })
            .concat(kind === 'pause' ? list : [])
            .map(function (a) { return { mid: a.t, len: a.len }; })
    );
    if (tl) tl.setAnchors(snapOn ? anchors : []);
    syncSnapBtn();
}

/** Допуск считается из ПИКСЕЛЕЙ ленты: при увеличении «8 px» — это другое
    число секунд, и фиксированный допуск в секундах мешал бы точной установке. */
function snapTolerance() {
    if (!tl || !state.main) return 0;
    var px = tl.pxPerSecond();
    return px > 0 ? (10 / px) : 0;
}
function maybeSnap(t) {
    if (!snapOn || !anchors.length) { lastSnap = null; return t; }
    var a = snap(anchors, t, snapTolerance());
    lastSnap = a;
    return a ? a.t : t;
}
function syncSnapBtn() {
    var b = $('vi-snap');
    if (!b) return;
    b.hidden = anchors.length === 0;
    b.setAttribute('aria-pressed', snapOn ? 'true' : 'false');
    b.title = (snapOn ? L.snap_on : L.snap_off) + ' — ' +
        (L.snap_found || '').replace(':n', String(anchors.length));
}
function showSnapHint() {
    var el = $('vi-snap-hint');
    if (!el) return;
    if (!lastSnap) { el.hidden = true; return; }
    el.textContent = lastSnap.kind === 'scene'
        ? L.snap_scene
        : (L.snap_pause || '').replace(':s', (lastSnap.len || 0).toFixed(1));
    el.hidden = false;
    clearTimeout(showSnapHint._t);
    showSnapHint._t = setTimeout(function () { el.hidden = true; }, 1400);
}

/** Короткая лента источника для мини-триммера карточки. */
async function buildClipStrip(c) {
    if (c.kind !== KINDS.VIDEO) return;
    c.strip = c.strip || []; c.onStrip = c.onStrip || [];
    var v = document.createElement('video');
    v.preload = 'auto'; v.muted = true; v.playsInline = true; v.src = c.url;
    await new Promise(function (r) { v.onloadedmetadata = r; v.onerror = r; setTimeout(r, 8000); });
    await filmstrip(v, c.srcDuration, 10, function (i, total, tile) {
        if (!tile) return;
        if (tile.width) c.strip[i] = tile;
        else { var im = new Image(); im.src = tile; c.strip[i] = im; im.onload = fire; }
        fire();
    });
    function fire() { (c.onStrip || []).forEach(function (f) { try { f(); } catch (e) { } }); }
    try { v.removeAttribute('src'); v.load(); } catch (e) { }
}

/* ─────────── добавление материала ─────────── */
function baseClip(place, at) {
    return {
        id: 'k' + (state.nextId++),
        place: place, at: at,
        anchor: 'start',         // 'start' | 'end' — от какого края ролика мерить `at`
        span: 0,                 // только для place:'replace' — сколько основного уходит
        in: 0, out: 0, srcDuration: 0, speed: 1,
        volume: 100, mute: false, duck: place === 'over' ? 60 : 0,
        fadeIn: 0, fadeOut: 0,
        gapBefore: 0, gapAfter: 0,
        transitionIn: { type: 'none', dur: 0.6 },
        transitionOut: { type: 'none', dur: 0.6 },
        fit: 'fit',
        x: 0.75, y: 0.75, w: 0.3, opacity: 100, radius: 12, shadow: true
    };
}

async function addVideoClip(file, place) {
    var at = currentMainTime();
    try {
        var m = await probeVideo(file);
        var c = Object.assign(baseClip(place, at), {
            kind: KINDS.VIDEO, file: file, url: m.url, name: m.name,
            srcDuration: m.duration, in: 0, out: m.duration,
            width: m.width, height: m.height, hasAudio: m.hasAudio,
            /* Заводим ДО первой отрисовки карточки: мини-триммер подписывается на
               `onStrip` в момент постройки, а лента приезжает позже. Если массив
               создать в buildClipStrip, подписка уходит в никуда и лента остаётся
               чёрной — так и было на первом прогоне. */
            strip: [], onStrip: []
        });
        if (!m.hasAudio) c.duck = 0;
        c.thumb = await grabThumb(m.el, Math.min(1, m.duration / 2), 160);
        pushUndo();
        state.clips.push(c);
        state.selected = c.id;
        insp.setOpen(c.id, true);
        pv.sync(); pv.select(c.id);
        renderAll(true);
        buildClipStrip(c);
        ae('feature_use', { f: 'clip_added', kind: 'video', place: place, dur: Math.round(m.duration) });
    } catch (e) {
        toast(L.clip_failed || L.error, 'error');
    }
}

async function addImageClip(file) {
    try {
        var m = await probeImage(file);
        var c = Object.assign(baseClip('cut', currentMainTime()), {
            kind: KINDS.IMAGE, file: file, url: m.url, img: m.img, name: m.name,
            hold: 3, srcDuration: 3, width: m.width, height: m.height, hasAudio: false,
            fit: 'blur', duck: 0, thumb: m.url
        });
        pushUndo();
        state.clips.push(c); state.selected = c.id;
        insp.setOpen(c.id, true);
        pv.sync(); pv.select(c.id); renderAll(true);
        ae('feature_use', { f: 'clip_added', kind: 'image' });
    } catch (e) { toast(L.clip_failed || L.error, 'error'); }
}

async function addFreeze() {
    if (!state.main) return;
    var at = currentMainTime();
    try {
        var bmp = await stillFrom(state.main.url, at, state.main.width, state.main.height);
        var cv = document.createElement('canvas');
        cv.width = state.main.width; cv.height = state.main.height;
        cv.getContext('2d').drawImage(bmp, 0, 0);
        var c = Object.assign(baseClip('cut', at), {
            kind: KINDS.FREEZE, bitmap: bmp, name: L.freeze_name + ' ' + fmtTimeMs(at),
            hold: 2, srcDuration: 2, width: state.main.width, height: state.main.height,
            hasAudio: false, duck: 0, fit: 'fit'
        });
        c.stillUrl = cv.toDataURL('image/jpeg', 0.8);
        c.thumb = c.stillUrl;
        pushUndo();
        state.clips.push(c); state.selected = c.id;
        insp.setOpen(c.id, true);
        pv.sync(); pv.select(c.id); renderAll(true);
        ae('feature_use', { f: 'clip_added', kind: 'freeze' });
    } catch (e) { toast(L.clip_failed || L.error, 'error'); }
}

function addCard() {
    if (!state.main) return;
    var c = Object.assign(baseClip('cut', currentMainTime()), {
        kind: KINDS.CARD, name: L.card_name, color: '#000000',
        hold: 1.5, srcDuration: 1.5, width: state.main.width, height: state.main.height,
        hasAudio: false, duck: 0,
        transitionIn: { type: 'fade', dur: 0.5 }, transitionOut: { type: 'fade', dur: 0.5 }
    });
    pushUndo();
    state.clips.push(c); state.selected = c.id;
    insp.setOpen(c.id, true);
    pv.sync(); pv.select(c.id); renderAll(true);
    ae('feature_use', { f: 'clip_added', kind: 'card' });
}

function removeClip(c) {
    pushUndo();
    state.clips = state.clips.filter(function (x) { return x.id !== c.id; });
    if (state.selected === c.id) state.selected = null;
    if (c.url) { try { URL.revokeObjectURL(c.url); } catch (e) { } }
    pv.sync(); renderAll(true);
    ae('feature_use', { f: 'clip_removed' });
}

/* Обновление БЕЗ пересборки карточек — для кадров живого перетаскивания. */
function lightRefresh() {
    if (!state.main) return;
    tl.setMarks(state.main, state.clips, state.selected);
    if (pv) pv.refresh();
    var out = outputDuration(state.main, state.clips);
    $('vi-tout').textContent = state.clips.length ? (L.result_is + ' ' + fmtTime(out)) : '';
    $('vi-go-dur').textContent = state.clips.length ? '· ' + fmtTime(out) : '';
}

/* ─────────── очередь пакета ─────────── */
function enqueue(files) {
    if (!files || !files.length) return;
    var added = 0;
    files.forEach(function (f) {
        if (!f || !/^video\//.test(f.type || '')) return;
        var key = f.name + '|' + f.size + '|' + f.lastModified;
        if (state.main && state.main.file && (state.main.file.name + '|' + state.main.file.size + '|' + state.main.file.lastModified) === key) return;
        if (state.queue.some(function (q) { return q.key === key; })) return;
        state.queue.push({ key: key, file: f, name: f.name || 'video', size: f.size });
        added++;
    });
    if (!added) return;
    renderQueue();
    renderAll();
    ae('feature_use', { f: 'batch_queued', n: state.queue.length });
    toast((L.batch_added || '').replace(':n', String(state.queue.length + 1)), 'info');
}
function dequeue(key) {
    state.queue = state.queue.filter(function (q) { return q.key !== key; });
    renderQueue();
    renderAll();
}
function renderQueue() {
    var btn = $('vi-queue-btn'), n = state.queue.length;
    btn.hidden = n === 0;
    $('vi-queue-n').textContent = String(n + (state.main ? 1 : 0));
    var list = $('vi-queue-list');
    list.textContent = '';
    var all = (state.main ? [{ key: '__main', name: state.main.name, size: state.main.size, main: true }] : []).concat(state.queue);
    all.forEach(function (q, i) {
        var row = document.createElement('div'); row.className = 'vi-queue-item';
        var num = document.createElement('span'); num.className = 'n'; num.textContent = (i + 1) + '.';
        var nm = document.createElement('b'); nm.textContent = q.name + (q.main ? ' · ' + (L.batch_reference || '') : '');
        row.append(num, nm);
        if (!q.main) {
            var x = document.createElement('button');
            x.type = 'button'; x.className = 'x'; x.textContent = '×';
            x.setAttribute('aria-label', L.remove);
            x.addEventListener('click', function () { dequeue(q.key); });
            row.appendChild(x);
        }
        list.appendChild(row);
    });
    $('vi-queue-title').textContent = (L.batch_title || '').replace(':n', String(all.length));
    var pro = window.__toolLimit && window.__toolLimit.isPro && window.__toolLimit.isPro();
    $('vi-queue-note').textContent = pro ? (L.batch_note_pro || '') : (L.batch_note_free || '');
}

/* ─────────── перерисовка ─────────── */
function renderAll(refit) {
    if (!state.main) return;
    tl.setDuration(state.main.duration);
    tl.setMarks(state.main, state.clips, state.selected);
    insp.render(state.main, state.clips, state.selected);
    if (pv) { pv.sync(); if (refit) pv.refresh(); else pv.refresh(); }
    var out = outputDuration(state.main, state.clips);
    $('vi-tout').textContent = state.clips.length ? (L.result_is + ' ' + fmtTime(out)) : '';
    $('vi-go-dur').textContent = state.clips.length ? '· ' + fmtTime(out) : '';
    var v = validate(state.main, state.clips);
    $('vi-go').disabled = !v.ok || !!exporting;
    $('vi-out-note').textContent = v.ok ? '' : (L['invalid_' + v.code] || '');
    syncHistoryButtons();
}

/* ─────────── таймлайн: клик и перетаскивание меток ─────────── */
function wireTimeline() {
    var box = $('vi-tlbox');
    var dragging = null;

    box.addEventListener('pointerdown', function (e) {
        var mark = e.target.closest && e.target.closest('.vi-mark');
        if (mark) {
            var c = byId(mark.dataset.id);
            if (!c) return;
            dragging = { c: c, el: mark };
            mark.classList.add('drag');
            state.selected = c.id; pv.select(c.id);
            tl.setMarks(state.main, state.clips, c.id);
            insp.render(state.main, state.clips, c.id);
            box.setPointerCapture(e.pointerId);
            e.preventDefault();
            return;
        }
        var t = maybeSnap(tl.timeAt(e.clientX));
        pv.seekMain(t);
        tl.setPlayhead(t);
        showSnapHint();
    });
    box.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        // ⚠️ Клип с якорем «от конца» хранит ОТСТУП, а не абсолютное время:
        // без перевода метка уехала бы в зеркальную точку при первом же рывке.
        var _t = maybeSnap(tl.timeAt(e.clientX));
        showSnapHint();
        dragging.c.at = (dragging.c.anchor === 'end') ? Math.max(0, state.main.duration - _t) : _t;
        tl.setMarks(state.main, state.clips, dragging.c.id);
        var lbl = $('vi-tout');
        lbl.textContent = L.result_is + ' ' + fmtTime(outputDuration(state.main, state.clips));
    });
    function endDrag(e) {
        if (!dragging) return;
        dragging.el.classList.remove('drag');
        pushUndo();
        dragging = null;
        try { box.releasePointerCapture(e.pointerId); } catch (x) { }
        renderAll(true);
        ae('feature_use', { f: 'mark_dragged' });
    }
    box.addEventListener('pointerup', endDrag);
    box.addEventListener('pointercancel', endDrag);
}

/* ─────────── переходы ─────────── */
var pickTarget = null;
function openTransitionPicker(clip, side, btn) {
    pickTarget = { clip: clip, side: side, btn: btn };
    var b = $('vi-tmodal-b');
    b.textContent = '';
    var cur = (side === 'in' ? clip.transitionIn : clip.transitionOut) || {};
    T.forEach(function (cat) {
        var h = document.createElement('div'); h.className = 'vi-tcat'; h.textContent = cat.cat;
        b.appendChild(h);
        var g = document.createElement('div'); g.className = 'vi-tgrid';
        cat.items.forEach(function (it) {
            var el = document.createElement('button');
            el.type = 'button'; el.className = 'vi-titem'; el.textContent = it.label;
            el.setAttribute('aria-pressed', cur.type === it.id ? 'true' : 'false');
            el.addEventListener('click', function () {
                var tr = side === 'in' ? clip.transitionIn : clip.transitionOut;
                tr.type = it.id;
                pushUndo();
                closePicker();
                renderAll(true);
                ae('feature_use', { f: 'transition_set', t: it.id });
            });
            g.appendChild(el);
        });
        b.appendChild(g);
    });
    $('vi-tmodal').classList.add('on');
}
function closePicker() { $('vi-tmodal').classList.remove('on'); pickTarget = null; }

/* ─────────── экспорт ─────────── */

/** Один прогон: собрать `main` и отдать Blob. Общий у одиночного и пакетного. */
async function renderOne(main, opts, hooks) {
    var target = chooseTarget(main, state.clips, opts.resolution);
    return await runExport(main, state.clips, Object.assign({}, opts, { target: target }), hooks);
}

/**
 * ПАКЕТ (Премиум). Клипы настроены на ПЕРВОМ ролике и применяются ко
 * всем: место каждого разрешается для КАЖДОГО ролика отдельно (`atOn`).
 * Отсюда и якорь «от конца»: без него аутро на двадцати роликах разной
 * длины пришлось бы ставить двадцать раз руками.
 *
 * ⚠️ Ролики разбираются ПО ОДНОМУ и отпускаются сразу: двадцать видео — это
 * двадцать декодированных дорожек, и держать их вместе нельзя (та же причина,
 * по которой `openBatch` в папочном лейне держит ОДИН файл).
 */
async function doBatchExport() {
    if (exporting) return;
    var v = validate(state.main, state.clips);
    if (!v.ok) { toast(L['invalid_' + v.code] || L.error, 'warning'); return; }

    var isPro = !!(window.__toolLimit && window.__toolLimit.isPro && window.__toolLimit.isPro());
    if (!isPro) {
        toast(L.batch_premium_only || '', 'warning');
        ae('feature_use', { f: 'batch_blocked_free', n: state.queue.length + 1 });
        return;
    }

    var jobs = [{ file: state.main.file, name: state.main.name }]
        .concat(state.queue.map(function (q) { return { file: q.file, name: q.name }; }));

    /* Папка/архив открывается ПЕРВЫМ await клика: право на выбор папки
       живёт на пользовательской активации, и любая загрузка до него её тратит. */
    var batch = null;
    try {
        batch = await window.__batchDeliver.openBatch({
            zipName: outBaseName() + '-' + jobs.length + '.zip',
            slug: SLUG, total: jobs.length
        });
    } catch (e) { batch = null; }
    /* ⛔ Запасного пути «скачать по одному» ЗДЕСЬ НЕТ, и это не упущение.
       Браузер отдаёт ОДИН файл на пользовательский жест: цикл __downloadFile
       придерживает второй и дальше отравляет вкладку, а тост при этом говорит
       «готово N». Замерено на этом же туле: два ролика — «Файл сохранён» один
       раз и «Готово роликов: 2» рядом. Лучше честно не начать. */
    if (!batch) {
        toast(L.batch_no_delivery || L.error, 'error');
        ae('feature_use', { f: 'batch_no_delivery' });
        return;
    }

    exporting = new AbortController();
    $('vi-work').hidden = false;
    $('vi-work-lane').textContent = '';
    $('vi-go').disabled = true;
    var okCount = 0, failed = [];
    var t0 = performance.now();
    stage('export_start');

    try {
        for (var i = 0; i < jobs.length; i++) {
            if (exporting.signal.aborted) break;
            var job = jobs[i];
            $('vi-work-t').textContent = (L.batch_working || '').replace(':i', String(i + 1)).replace(':n', String(jobs.length));
            $('vi-work-s').textContent = job.name;
            $('vi-bar').style.width = '0%';

            var run = null;
            if (window.__toolLimit) {
                // Единица — ВЫДАННОЕ видео. У пакета их столько, сколько роликов.
                var keys = window.__toolLimit.fileKeys(
                    [job.file].concat(state.clips.map(function (c) { return c.file; }).filter(Boolean))) || [];
                run = window.__toolLimit.begin(SLUG, 'set:' + keys.slice().sort().join('|'));
                if (!run) { failed.push(job.name); break; }
            }
            try {
                var probed = (i === 0) ? state.main : await probeVideo(job.file);
                var res = await renderOne(probed, {
                    format: $('vi-fmt').value === 'same' ? 'mp4' : $('vi-fmt').value,
                    quality: $('vi-q').value,
                    resolution: $('vi-res').value
                }, {
                    signal: exporting.signal,
                    onProgress: function (f) {
                        $('vi-bar').style.width = Math.round(Math.max(0, Math.min(1, f)) * 100) + '%';
                    },
                    onLaneFallback: function (why) {
                        $('vi-work-lane').textContent = /^hw:container:/.test(String(why)) ? L.lane_container : L.lane_wasm;
                    }
                });
                await batch.write(outNameFor(probed.name), res.blob);
                if (run) run.commit();
                okCount++;
                if (i > 0 && probed.url) { try { URL.revokeObjectURL(probed.url); } catch (e) { } }
            } catch (e) {
                if (run) run.abort();
                if (e && e.name === 'AbortError') break;
                failed.push(job.name);
            }
        }
        try { await batch.finish(); } catch (e) { }
        ae('tool_complete', {
            mode: 'batch', files: jobs.length, ok: okCount, failed: failed.length,
            ms: Math.round(performance.now() - t0), clips: state.clips.length
        });
        if (okCount) toast((L.batch_done || '').replace(':n', String(okCount)), 'success');
        if (failed.length) toast((L.batch_failed || '').replace(':n', String(failed.length)), 'error');
    } finally {
        exporting = null;
        $('vi-work').hidden = true;
        renderAll();
    }
}

async function doExport() {
    if (exporting) return;
    if (state.queue.length) return doBatchExport();
    var v = validate(state.main, state.clips);
    if (!v.ok) { toast(L['invalid_' + v.code] || L.error, 'warning'); return; }

    /* Единица — ОДНО СОБРАННОЕ ВИДЕО, а не входной файл, поэтому ключ
       СОСТАВНОЙ (`set:` из отсортированных ключей), как у /split-screen.
       ⚠️ Массив ключей списывал бы за КАЖДЫЙ файл: у этого тула вход всегда
       минимум два (основное видео + вставка), поэтому при капе 2 монтаж с
       ДВУМЯ вставками стоил три единицы и отвергался — то есть кап отказывал
       туда, куда тул и существует. Воспроизведено: 4 клипа → «Видео на
       сегодня закончились» на первом же нажатии.
       Повтор того же набора сегодня остаётся бесплатным — это и есть обещание
       со стены («повторная сборка того же материала»). */
    var files = [state.main.file].concat(state.clips.map(function (c) { return c.file; }).filter(Boolean));
    var run = null;
    if (window.__toolLimit) {
        var keys = window.__toolLimit.fileKeys(files) || [];
        var composite = 'set:' + keys.slice().sort().join('|');
        run = window.__toolLimit.begin(SLUG, composite);
        if (!run) return;                 // стена уже показана примитивом
    }

    pv.pause();
    exporting = new AbortController();
    $('vi-work').hidden = false;
    $('vi-work-t').textContent = L.working;
    $('vi-work-s').textContent = L.stage_prepare;
    $('vi-work-lane').textContent = '';
    $('vi-bar').style.width = '0%';
    $('vi-go').disabled = true;
    stage('export_start');
    var t0 = performance.now();

    try {
        var target = chooseTarget(state.main, state.clips, $('vi-res').value);
        var res = await runExport(state.main, state.clips, {
            format: $('vi-fmt').value === 'same' ? 'mp4' : $('vi-fmt').value,
            quality: $('vi-q').value,
            resolution: $('vi-res').value,
            target: target
        }, {
            signal: exporting.signal,
            onProgress: function (f) {
                var p = Math.round(Math.max(0, Math.min(1, f)) * 100);
                $('vi-bar').style.width = p + '%';
                $('vi-work-s').textContent = L.stage_encode + ' ' + p + '%';
            },
            onLaneFallback: function (why) {
                /* Причина бывает ДВУХ разных сортов, и путать их нельзя:
                   «браузер не умеет» — это про машину человека, а «этот
                   контейнер собирает только FFmpeg» — про его же выбор в
                   списке. Второе не повод пугать словом «недоступен». */
                $('vi-work-lane').textContent = /^hw:container:/.test(String(why)) ? L.lane_container : L.lane_wasm;
                ae('feature_use', { f: 'lane_fallback', why: String(why).slice(0, 60) });
            },
            onFormatFallback: function (f) { toast(L.webm_fallback || '', 'warning'); }
        });

        if (exporting.signal.aborted) return;
        if (resultUrl) URL.revokeObjectURL(resultUrl);
        resultUrl = URL.createObjectURL(res.blob);
        $('vi-done-video').src = resultUrl;
        $('vi-done-meta').textContent = ($('vi-fmt').value || 'mp4').toUpperCase() + ' · ' +
            target.w + '×' + target.h + ' · ' + fmtTime(res.duration) + ' · ' + fmtSize(res.blob.size);
        $('vi-done').hidden = false;
        window.__viResult = res.blob;
        if (run) run.commit();
        ae('tool_complete', {
            lane: res.lane, clips: state.clips.length,
            cuts: cuts(state.clips).length, overs: overlays(state.clips).length,
            ms: Math.round(performance.now() - t0),
            outSec: Math.round(res.duration), mb: +(res.blob.size / 1048576).toFixed(1)
        });
        try { window.__aeToolComplete && window.__aeToolComplete({ lane: res.lane, clips: state.clips.length }); } catch (e) { }
        if (window.__announce) window.__announce(L.result_ready);
    } catch (e) {
        if (run) run.abort();
        if (!(e && e.name === 'AbortError')) {
            var msg = String((e && e.message) || e);
            toast(/memory|OOM|out of bounds/i.test(msg) ? (L.oom_error) : (L.error + ': ' + msg.slice(0, 160)), 'error');
            try { window.__aeToolError && window.__aeToolError({ error_type: 'vi_export', err_msg: msg.slice(0, 500) }); } catch (x) { }
        }
    } finally {
        exporting = null;
        $('vi-work').hidden = true;
        renderAll();
    }
}

/* ─────────── клавиши ─────────── */
function wireKeys() {
    document.addEventListener('keydown', function (e) {
        if (!state.main || $('vi-app').hidden) return;
        var tag = (e.target.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable) return;
        var mod = e.ctrlKey || e.metaKey;
        var kl = window.__keyLetter ? window.__keyLetter(e) : (/^[a-z]$/i.test(e.key || '') ? e.key.toLowerCase() : '');
        if (mod && kl === 'z') { e.preventDefault(); e.shiftKey ? redo() : undo(); return; }
        if (mod && kl === 'y') { e.preventDefault(); redo(); return; }
        if (mod) return;
        switch ((kl && (e.ctrlKey || e.metaKey || e.altKey)) ? '' : (kl || e.key)) {
            case ' ': e.preventDefault(); pv.toggle(); break;
            case 's': pv.stop(); break;
            case 'i': $('vi-pick-cut').click(); break;
            case 'o': $('vi-pick-over').click(); break;
            case 'ArrowLeft': e.preventDefault(); pv.seek(Math.max(0, pv.time - (e.shiftKey ? 5 : 1 / 30))); break;
            case 'ArrowRight': e.preventDefault(); pv.seek(pv.time + (e.shiftKey ? 5 : 1 / 30)); break;
            case 'Delete': case 'Backspace':
                if (state.selected) { var c = byId(state.selected); if (c) removeClip(c); }
                break;
            case 'Escape': closePicker(); break;
        }
    });
}

/* ─────────── попапы (Popover API + запасной путь) ─────────── */
function wirePopover(btn, panel) {
    var native = typeof panel.showPopover === 'function' && panel.hasAttribute('popover');
    /* ⚠️ `hidden` и Popover API несовместимы: showPopover() снимает своё
       `display:none`, но наше `.vi-keys[hidden]{display:none!important}`
       сильнее — попап «открывался» нулевой коробкой. Атрибут нужен ТОЛЬКО
       запасному пути, поэтому у родного его снимаем сразу (UA-правило
       `[popover]:not(:popover-open)` прячет панель и без него). */
    if (native) panel.removeAttribute('hidden');
    btn.addEventListener('click', function (e) {
        e.stopPropagation();
        if (native) {
            var open = panel.matches(':popover-open');
            open ? panel.hidePopover() : panel.showPopover();
            btn.setAttribute('aria-expanded', String(!open));
            if (!open) {
                var r = btn.getBoundingClientRect();
                panel.style.position = 'fixed'; panel.style.margin = '0';
                panel.style.top = Math.min(window.innerHeight - 20, r.bottom + 6) + 'px';
                panel.style.left = Math.max(8, Math.min(window.innerWidth - 340, r.left - 240)) + 'px';
            }
            return;
        }
        // Без Popover API — тот же блок рядом с кнопкой.
        panel.classList.add('fallback');
        var willOpen = panel.hidden;
        panel.hidden = !willOpen;
        btn.setAttribute('aria-expanded', String(willOpen));
        if (willOpen) {
            var host = btn.closest('.vi-app') || document.body;
            if (panel.parentElement !== host) host.appendChild(panel);
        }
    });
    document.addEventListener('click', function (ev) {
        if (native || panel.hidden) return;
        // composedPath, а не contains: после перерисовки узел мог смениться —
        // тот же класс дефекта, что чинили на /thumbnail-maker.
        var path = ev.composedPath ? ev.composedPath() : [ev.target];
        if (path.indexOf(panel) >= 0 || path.indexOf(btn) >= 0) return;
        panel.hidden = true;
        btn.setAttribute('aria-expanded', 'false');
    });
}

/* ─────────── старт ─────────── */
function init() {
    var drop = $('vi-drop');
    if (!drop) return;

    /* ⚠️ Не голый addEventListener: этот модуль отложенный (type=module), и
       файл, выбранный ДО его выполнения, событие уже отправил — слушатель
       пришёл бы на пустое место. `__bindUploaderFile` доигрывает
       `el.__pendingFile` опоздавшему подписчику; сырой слушатель остаётся
       ровно как запасная половина (UploaderListenerRaceTest). */
    async function takeFiles(f, detail) {
        var all = (detail && detail.files && detail.files.length) ? Array.prototype.slice.call(detail.files) : [f];
        if (!all.length) return;
        if (!state.main) {
            // ⚠️ ЖДЁМ загрузку основного, а не запускаем параллельно: enqueue
            // сверяется с `state.main`, и без await он видит null — счётчик
            // показывал «1» там, где роликов уже два, а повторно выбранный тот
            // же файл попал бы в очередь как чужой.
            await loadMain(all[0]);
            enqueue(all.slice(1));
        } else {
            enqueue(all);
        }
    }
    if (typeof window.__bindUploaderFile === 'function') {
        window.__bindUploaderFile(drop, takeFiles);
    } else {
        drop.addEventListener('tool-uploader:file', function (e) {
            var f = e.detail && e.detail.file;
            if (f) takeFiles(f, e.detail);
        });
    }
    drop.addEventListener('tool-uploader:clear', function () { reset(); });
    drop.addEventListener('tool-uploader:rerouted', function (e) {
        // Картинка на зоне основного видео — почти всегда «вставить фото».
        if (!state.main) toast(L.image_needs_main || '', 'warning');
    });

    $('vi-pick-cut').addEventListener('change', function () {
        if (this.files && this.files[0]) addVideoClip(this.files[0], 'cut');
        this.value = '';
    });
    $('vi-pick-over').addEventListener('change', function () {
        if (this.files && this.files[0]) addVideoClip(this.files[0], 'over');
        this.value = '';
    });
    $('vi-pick-img').addEventListener('change', function () {
        if (this.files && this.files[0]) { if (state.main) addImageClip(this.files[0]); }
        this.value = '';
    });

    $('vi-add-cut').addEventListener('click', function () { $('vi-pick-cut').click(); });
    $('vi-add-over').addEventListener('click', function () { $('vi-pick-over').click(); });
    function closeMore() {
        var m = $('vi-more');
        try { if (m.matches(':popover-open')) m.hidePopover(); } catch (e) { }
        if (!m.hasAttribute('popover') || typeof m.showPopover !== 'function') m.hidden = true;
        $('vi-add-more').setAttribute('aria-expanded', 'false');
    }
    $('vi-add-photo').addEventListener('click', function () { closeMore(); $('vi-pick-img').click(); });
    $('vi-add-freeze').addEventListener('click', function () { closeMore(); addFreeze(); });
    $('vi-add-card').addEventListener('click', function () { closeMore(); addCard(); });

    wirePopover($('vi-add-more'), $('vi-more'));
    wirePopover($('vi-help'), $('vi-keys'));
    wirePopover($('vi-queue-btn'), $('vi-queue'));

    $('vi-play').addEventListener('click', function () { pv && pv.toggle(); });
    $('vi-stop').addEventListener('click', function () { pv && pv.stop(); });
    $('vi-snap').addEventListener('click', function () {
        snapOn = !snapOn;
        tl && tl.setAnchors(snapOn ? anchors : []);
        syncSnapBtn();
        try { window.__toolSettings && window.__toolSettings.save(SLUG, 'snap', snapOn ? '1' : '0'); } catch (e) { }
        ae('feature_use', { f: 'snap_toggled', on: snapOn ? 1 : 0 });
    });
    try {
        var saved = window.__toolSettings && window.__toolSettings.load(SLUG, 'snap', '1');
        if (saved === '0') snapOn = false;
    } catch (e) { }
    $('vi-zin').addEventListener('click', function () { tl && setZoom(tl.zoom * 1.6); });
    $('vi-zout').addEventListener('click', function () { tl && setZoom(tl.zoom / 1.6); });
    $('vi-undo').addEventListener('click', undo);
    $('vi-redo').addEventListener('click', redo);
    $('vi-go').addEventListener('click', doExport);
    $('vi-abort').addEventListener('click', function () { if (exporting) { exporting.abort(); try { window.FFmpegProcessor && window.FFmpegProcessor.cancel(); } catch (e) { } } });
    $('vi-tmodal-x').addEventListener('click', closePicker);
    $('vi-tmodal').addEventListener('click', function (e) { if (e.target === this) closePicker(); });
    $('vi-back').addEventListener('click', function () { $('vi-done').hidden = true; });
    $('vi-save').addEventListener('click', function () {
        if (!window.__viResult) return;
        var name = outName();
        if (window.__downloadFile) window.__downloadFile(window.__viResult, name);
        ae('feature_use', { f: 'downloaded' });
    });

    $('vi-fmt').addEventListener('change', function () { renderAll(); });
    $('vi-res').addEventListener('change', function () { renderAll(); });

    if (window.ResolutionModule && !window.ResolutionModule.__isStub) {
        window.ResolutionModule.init({ tool: SLUG, selectId: 'vi-res', badgeId: 'vi-res-badge' });
    } else {
        document.addEventListener('ResolutionModule:ready', function once() {
            document.removeEventListener('ResolutionModule:ready', once);
            if (window.ResolutionModule) window.ResolutionModule.init({ tool: SLUG, selectId: 'vi-res', badgeId: 'vi-res-badge' });
        });
    }

    document.addEventListener('visibilitychange', function () { if (document.hidden && pv && pv.playing) pv.pause(); });
}

function setZoom(z) { tl.setZoom(z); $('vi-zlv').textContent = Math.round(tl.zoom * 10) / 10 + '×'; tl.setMarks(state.main, state.clips, state.selected); }

/**
 * Имя выхода — из МАТЕРИАЛА, а не константа: за сессию человек экспортирует
 * несколько раз, и «video-insert.mp4 (1) (2)» открывают не тот файл.
 */
function outBaseName() {
    return (state.main && state.main.name || 'video').replace(/\.[^.]+$/, '');
}
/* Имя выхода строится из имени ИСХОДНОГО ролика: в пакете иначе
   получится двадцать файлов с одинаковым названием и «(1)(2)(3)» поверх. */
function outNameFor(sourceName) {
    var fmt = $('vi-fmt').value === 'same' ? 'mp4' : $('vi-fmt').value;
    var base = String(sourceName || 'video').replace(/\.[^.]+$/, '');
    var n = state.clips.length;
    var suffix = '-' + (L.name_suffix || 'insert') + (n > 1 ? '-' + n : '');
    if (window.__outputName) {
        return window.__outputName.join([window.__outputName.sanitize(base + suffix)], fmt);
    }
    return (base + suffix).slice(0, 60) + '.' + fmt;
}
function outName() { return outNameFor(state.main && state.main.name); }

function reset() {
    if (pv) pv.destroy();
    state.clips.forEach(function (c) { if (c.url) { try { URL.revokeObjectURL(c.url); } catch (e) { } } });
    if (state.main && state.main.url) { try { URL.revokeObjectURL(state.main.url); } catch (e) { } }
    if (resultUrl) { try { URL.revokeObjectURL(resultUrl); } catch (e) { } resultUrl = null; }
    state = { main: null, clips: [], selected: null, nextId: 1, queue: [] };
    try { renderQueue(); } catch (e) { }
    undoStack = []; redoStack = [];
    if (anchorAbort) { anchorAbort.abort(); anchorAbort = null; }
    anchors = []; lastSnap = null;
    tl = null; pv = null; insp = null;
    $('vi-app').hidden = true;
    $('vi-done').hidden = true;
    $('vi-tlload').hidden = false;
    var v = $('vi-video'); try { v.removeAttribute('src'); v.load(); } catch (e) { }
    $('vi-frame').querySelectorAll('.vi-pip,.vi-guide').forEach(function (n) { n.remove(); });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
